import { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router';
import { AuthProvider } from './auth/AuthContext';
import { MeProvider } from './auth/MeContext';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { RequireFlag } from './auth/RequireFlag';
import { RequireSection } from './auth/RequireSection';
import { LoginPage } from './pages/Login/LoginPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { DashboardPage } from './pages/Dashboard';
import { PacksPage } from './pages/Packs';
import { MyPacksPage, NewPackPage, PackPage } from './pages/MyPacks';
import { ConfigsPage } from './pages/Configs';
import { CategoryConfigsPage } from './pages/CategoryConfigs';
import { FooterLinksPage } from './pages/FooterLinks';
import { ThemeEditorPage } from './pages/Themes/ThemeEditorPage';
import { DefaultTemplateRedirect } from './pages/Themes/DefaultTemplateRedirect';
import { AnimationPage } from './pages/Animation';
import IntegrationPage from './pages/Integration/IntegrationPage';
import { SessionsPage } from './pages/Sessions/SessionsPage';
import { SessionDetailPage } from './pages/Sessions/SessionDetailPage';
import { ShippingsPage } from './pages/Shippings/ShippingsPage';
import { ShippingDetailPage } from './pages/Shippings/ShippingDetailPage';
import { ShippingPromotionsPage } from './pages/ShippingPromotions';
import { CallbackLogsPage } from './pages/CallbackLogs/CallbackLogsPage';
import { TestPlayersPage } from './pages/TestPlayers';
import { ApiPage } from './pages/Api';
import { SecurityPage } from './pages/Security/SecurityPage';
import { TeamPage } from './pages/Team';
import { WebhooksPage } from './pages/Webhooks';
import { TrackingPixelsPage } from './pages/TrackingPixels';
import { MediaKitPage, MediaKitPackPage } from './pages/MediaKit';
import { AppShell } from './components/AppShell';
import { ChunkBoundary } from './components/ChunkBoundary';

// Split out on its own: Metrics is the only screen that needs a charting
// library, and bundling recharts into the entry chunk made every other screen
// pay for it. Loaded when the operator opens the tab.
const MetricsPage = lazy(() =>
  import('./pages/Metrics').then((module) => ({ default: module.MetricsPage })),
);

// Lazy for the same reason as the screen it documents rather than for its own
// weight: it is reached from Metrics, so it has no business in the entry chunk
// that every other page pays for.
const MetricsReference = lazy(() =>
  import('./pages/Metrics/MetricsReference').then((module) => ({
    default: module.MetricsReference,
  })),
);

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <AuthProvider>
        <ProtectedRoute>
          <MeProvider>
            <AppShell />
          </MeProvider>
        </ProtectedRoute>
      </AuthProvider>
    ),
    children: [
      // Dashboard and the API docs are open to every console user, so a
      // member holding nothing still lands somewhere real.
      { path: '/', element: <DashboardPage /> },
      {
        path: '/packs',
        element: (
          <RequireSection section="packs">
            <PacksPage />
          </RequireSection>
        ),
      },
      {
        // Self-service pack builder. The operator flag is handled in the page
        // itself (404 → back to the dashboard), like Packs.
        path: '/my-packs',
        element: (
          <RequireSection section="pack_builder">
            <MyPacksPage />
          </RequireSection>
        ),
      },
      {
        path: '/my-packs/new',
        element: (
          <RequireSection section="pack_builder">
            <NewPackPage />
          </RequireSection>
        ),
      },
      {
        path: '/my-packs/:id',
        element: (
          <RequireSection section="pack_builder">
            <PackPage />
          </RequireSection>
        ),
      },
      {
        // Original marketing artwork. The operator flag says the company has
        // the feature, the section says this person may download from it.
        path: '/media-kit',
        element: (
          <RequireSection section="media_kit">
            <RequireFlag flag="allow_media_kit">
              <MediaKitPage />
            </RequireFlag>
          </RequireSection>
        ),
      },
      {
        path: '/media-kit/:packId',
        element: (
          <RequireSection section="media_kit">
            <RequireFlag flag="allow_media_kit">
              <MediaKitPackPage />
            </RequireFlag>
          </RequireSection>
        ),
      },
      // Behind the same section guard as the screen it documents: the
      // definitions are only meaningful to someone who can see the figures.
      {
        path: '/metrics/reference',
        element: (
          <RequireSection section="metrics">
            <ChunkBoundary>
              <Suspense
                fallback={
                  <div className="p-6 text-center text-sm text-[var(--co-muted)]">Loading…</div>
                }
              >
                <MetricsReference />
              </Suspense>
            </ChunkBoundary>
          </RequireSection>
        ),
      },
      {
        // The operator flag is handled in the page itself (404 → back to the
        // dashboard), like Packs.
        path: '/metrics',
        element: (
          <RequireSection section="metrics">
            {/* The boundary sits ABOVE Suspense on purpose: what it catches is
                the chunk failing to arrive, which throws past the fallback. */}
            <ChunkBoundary>
              <Suspense
                fallback={
                  <div className="p-6 text-center text-sm text-[var(--co-muted)]">
                    Loading metrics…
                  </div>
                }
              >
                <MetricsPage />
              </Suspense>
            </ChunkBoundary>
          </RequireSection>
        ),
      },
      {
        // Bare template pages land on the operator's default template — the
        // one their players get — never on a fixed one.
        path: '/themes',
        element: (
          <RequireSection section="themes">
            <DefaultTemplateRedirect base="/themes" />
          </RequireSection>
        ),
      },
      {
        path: '/themes/:template',
        element: (
          <RequireSection section="themes">
            <ThemeEditorPage />
          </RequireSection>
        ),
      },
      {
        // Pack Opening — the opening mechanic per template. Same section as
        // Themes: it is one more part of the template's look. The feature
        // itself is granted per operator.
        path: '/animation',
        element: (
          <RequireSection section="themes">
            <RequireFlag flag="allow_animation_settings">
              <DefaultTemplateRedirect base="/animation" />
            </RequireFlag>
          </RequireSection>
        ),
      },
      {
        path: '/animation/:template',
        element: (
          <RequireSection section="themes">
            <RequireFlag flag="allow_animation_settings">
              <AnimationPage />
            </RequireFlag>
          </RequireSection>
        ),
      },
      {
        path: '/configs',
        element: (
          <RequireSection section="offers">
            <ConfigsPage />
          </RequireSection>
        ),
      },
      {
        path: '/category-configs',
        element: (
          <RequireSection section="categories">
            <CategoryConfigsPage />
          </RequireSection>
        ),
      },
      {
        path: '/footer-links',
        element: (
          <RequireSection section="links">
            <FooterLinksPage />
          </RequireSection>
        ),
      },
      {
        path: '/sessions',
        element: (
          <RequireSection section="players">
            <SessionsPage />
          </RequireSection>
        ),
      },
      {
        path: '/sessions/:id',
        element: (
          <RequireSection section="players">
            <SessionDetailPage />
          </RequireSection>
        ),
      },
      {
        path: '/shippings',
        element: (
          <RequireSection section="shipping">
            <ShippingsPage />
          </RequireSection>
        ),
      },
      {
        path: '/shippings/:id',
        element: (
          <RequireSection section="shipping">
            <ShippingDetailPage />
          </RequireSection>
        ),
      },
      {
        // Operator-funded delivery prices: the operator flag says the company
        // has the feature, the section says this person may manage it.
        path: '/shipping-promotions',
        element: (
          <RequireSection section="shipping">
            <RequireFlag flag="allow_shipping_promotions">
              <ShippingPromotionsPage />
            </RequireFlag>
          </RequireSection>
        ),
      },
      {
        path: '/callback-logs',
        element: (
          <RequireSection section="logs">
            <CallbackLogsPage />
          </RequireSection>
        ),
      },
      {
        path: '/test-players',
        element: (
          <RequireSection section="test_players">
            <TestPlayersPage />
          </RequireSection>
        ),
      },
      {
        path: '/integration',
        element: (
          <RequireSection section="integration">
            <IntegrationPage />
          </RequireSection>
        ),
      },
      {
        // Grantable, unlike Integration: wiring up notifications moves no
        // money, so an owner can hand it to whoever integrates.
        path: '/webhooks',
        element: (
          <RequireSection section="webhooks">
            <WebhooksPage />
          </RequireSection>
        ),
      },
      {
        // Partner ad pixels: the operator flag says the company has the
        // feature, the section says this person may edit it.
        path: '/tracking-pixels',
        element: (
          <RequireSection section="tracking">
            <RequireFlag flag="allow_tracking_pixels">
              <TrackingPixelsPage />
            </RequireFlag>
          </RequireSection>
        ),
      },
      {
        path: '/api',
        element: <ApiPage />,
      },
      {
        // No section gate: everyone manages their own sign-in IPs and their
        // own 2FA here. The operator-wide half of the page is owner-only and
        // the backend simply does not send it to anyone else.
        path: '/security',
        element: <SecurityPage />,
      },
      {
        path: '/team',
        element: (
          <RequireSection section="team">
            <TeamPage />
          </RequireSection>
        ),
      },
    ],
  },
  {
    path: '/login',
    element: (
      <AuthProvider>
        <LoginPage />
      </AuthProvider>
    ),
  },
  {
    path: '/change-password',
    element: (
      <AuthProvider>
        <ProtectedRoute>
          <ChangePasswordPage />
        </ProtectedRoute>
      </AuthProvider>
    ),
  },
  { path: '*', element: <Navigate to="/" replace /> },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
